import { Box, Stack, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { Grid } from '@mui/material';
import { graphql, FragmentType, getFragmentData } from '../../gql';
import { useApiMutation } from '../../query/hooks';
import { alpha, useTheme } from '@mui/material/styles';
import getOodColor from '../../utils/getOodColor';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useIntl } from 'react-intl';
import { getNightOrDayPalette } from '../../utils/getNightOrDayPalette';
import { useSnackbar } from '../../store/snackbar';

const personaBlockItems_itemFragment = graphql(`
	fragment personaBlockItems_itemFragment on PersonaBlockItem {
		id
		name
		value
	}
`);

type PersonaBlockItemFragment = FragmentType<typeof personaBlockItems_itemFragment>;

const personaDetailBlockItem_updatePersonaBlockItem = graphql(`
	mutation personaDetailBlockItem_updatePersonaBlockItem($id: UUID!, $data: Int!) {
		updatePersonaBlockItem(by: { id: $id }, data: { value: $data }) {
			ok
			errorMessage
		}
	}
`);

type Props = Readonly<{
	itemsFragment: readonly PersonaBlockItemFragment[];
	color: string;
}>;

const PersonaBlockItems: FC<Props> = ({ itemsFragment, color }) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();
	const { mutate: updatePersonaBlockItem } = useApiMutation(personaDetailBlockItem_updatePersonaBlockItem, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updatePersonaBlockItem.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.updatePersonaBlockItem.errorMessage,
				openSnackbar,
			});
		},
	});
	const items = getFragmentData(personaBlockItems_itemFragment, itemsFragment);
	const [hoveredIndexes, setHoveredIndexes] = useState<Array<number | null>>(Array(items.length).fill(null));
	const theme = useTheme();

	const handleUpdateItem = (itemId: string, value: number) => {
		updatePersonaBlockItem({ id: itemId, data: value });
	};

	const handleMouseEnter = (rowIndex: number, columnIndex: number) => {
		setHoveredIndexes((prevIndexes) => {
			const newIndexes = [...prevIndexes];
			newIndexes[rowIndex] = columnIndex;
			return newIndexes;
		});
	};

	const handleMouseLeave = (rowIndex: number) => {
		setHoveredIndexes((prevIndexes) => {
			const newIndexes = [...prevIndexes];
			newIndexes[rowIndex] = null;
			return newIndexes;
		});
	};

	return (
		<Stack width={'100%'} gap={1.5}>
			{items.map((item, index) => {
				return (
					<Grid container alignItems={'center'} key={index}>
						<Grid item xs={6}>
							<Typography variant={'h6'} fontWeight={500}>
								{item.name}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Stack direction={'row'} gap={'3px'} marginLeft={3}>
								{Array.from({ length: 5 }, (_, i) => (
									<Stack
										key={i}
										sx={{
											cursor: 'pointer',
										}}
										onClick={() => handleUpdateItem(item.id, i + 1)}
										onMouseEnter={() => handleMouseEnter(index, i + 1)}
										onMouseLeave={() => handleMouseLeave(index)}
										width={'100%'}
										alignItems={'start'}
										maxWidth={37}
									>
										<Box
											height={8}
											width={'100%'}
											borderRadius={'2px'}
											sx={{
												opacity: i < hoveredIndexes[index]! ? 0.625 : 1,
												transition: 'all 0.15s ease',
												backgroundColor:
													i < item.value || i < hoveredIndexes[index]!
														? getOodColor(theme, color).content
														: alpha(theme.palette[getNightOrDayPalette(theme)][300] as string, 0.62),
											}}
										/>
									</Stack>
								))}
							</Stack>
						</Grid>
					</Grid>
				);
			})}
		</Stack>
	);
};

export default PersonaBlockItems;
